import { Calendar, getCalendarEntry, getCalendarIdByFolderId } from 'owa-calendar-cache';
import {
    calendarNotificationsBarUpdateCalendar,
    calendarNotificationsBarUpdateCalendarFailed,
} from './updateFolder.locstring.json';
import loc, { format } from 'owa-localize';

import { CalendarEntry } from 'owa-graph-schema';
import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import { ControlIcons } from 'owa-control-icons';
import SetFolderField from 'owa-service/lib/contract/SetFolderField';
import { action } from 'satcheljs/lib/legacy';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import folderChange from 'owa-service/lib/factory/folderChange';
import folderId from 'owa-service/lib/factory/folderId';
import { getCalendars } from 'owa-calendar-surface-calendars-store';
import getUpdateForFolderProperty from '../utils/getUpdateForFolderProperty';
import handleCalendarServiceError from './handleCalendarServiceError';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';
import { updateCalendarFolderAndCache } from '../utils/updateCalendarFolderAndCache';
import updateFolderService from '../services/updateFolderService';

/**
 * The action worker which handles updating a calendar folder.
 * @param calendarFolderUpdates calendarFolder object with the list of properties that changed and will be sent to the server
 */

export let updateFolder = action('updateFolder')(function updateFolderInternal(
    calendarFolderUpdates: CalendarFolder
): Promise<void> {
    let updates = [];

    if (!calendarFolderUpdates) {
        return null;
    }

    Object.keys(calendarFolderUpdates).map(key => {
        let update: SetFolderField = getUpdateForFolderProperty(key, calendarFolderUpdates[key]);
        if (update) {
            updates.push(update);
        }
    });

    const calendars = getCalendars();
    const foundCalendarResult = findInArray(calendars, item => {
        let calendarId = getCalendarIdByFolderId(calendarFolderUpdates.FolderId.Id);
        return item.id.id == calendarId.id;
    });
    const calendar =
        foundCalendarResult && foundCalendarResult.index >= 0
            ? calendars[foundCalendarResult.index]
            : undefined;
    const calendarName =
        foundCalendarResult && foundCalendarResult.index >= 0
            ? calendars[foundCalendarResult.index].displayName
            : undefined;

    // we have to assign the folder because getCalendarEntry returns the actual reference
    // to the mobx store value, which can get updated externally
    let oldCalendarEntryWithFolderProps: CalendarEntry = { ...getCalendarEntry(calendar) };

    return handleCalendarServiceError(
        constructRequestCallback(calendar, calendarFolderUpdates, calendarName, updates),
        constructRequestFailedCallback(
            calendar,
            oldCalendarEntryWithFolderProps,
            calendarFolderUpdates,
            calendarName
        )
    );
});

function constructRequestCallback(
    calendar: Calendar,
    calendarFolderUpdates: CalendarFolder,
    calendarName: string,
    updates: SetFolderField[]
) {
    return async () => {
        updateCalendarFolderAndCache(calendar, calendarFolderUpdates);

        lazyShowCalendarNotification.import().then(showCalendarNotification => {
            showCalendarNotification(
                'updateFolder',
                'Surface',
                format(loc(calendarNotificationsBarUpdateCalendar), calendarName),
                { icon: ControlIcons.Save }
            );
        });

        return updateFolderService(
            folderChange({
                FolderId: folderId(calendarFolderUpdates.FolderId),
                Updates: updates,
            }),
            calendar.id.mailboxInfo
        ).then(response => {
            return response;
        });
    };
}

function constructRequestFailedCallback(
    calendar: Calendar,
    oldCalendarEntryWithFolderProps: CalendarEntry,
    calendarFolderUpdates: CalendarFolder,
    calendarName: string
) {
    return () => {
        updateCalendarFolderAndCache(calendar, oldCalendarEntryWithFolderProps);

        showCalendarErrorNotification(
            'updateFolder',
            format(loc(calendarNotificationsBarUpdateCalendarFailed), calendarName),
            ControlIcons.Error,
            loc(tryAgainLabel),
            () => updateFolder(calendarFolderUpdates)
        );
    };
}

export default updateFolder;
